import { PageSection } from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { PatientList } from '@app/components'
import { usePatientsSearch } from '@app/domain/patient/patient.hooks'
import type { IPatient } from '@app/domain/patient/patient.interface'
import type { AppState } from '@app/state'
import * as PatientListState from '@app/state/patient-list'

export const ClinicPatientList = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const {
    birthSexFilter,
    dateOfBirthFilter,
    mrnInputFilter,
    nameInputFilter,
    pageNumber,
    recordsPerPage,
    sortDirection,
    sortField,
  } = useSelector<AppState, PatientListState.State>(
    (state) => state.patientList,
  )

  const { patients, totalNumberOfRecords, getPatients } = usePatientsSearch()

  const onRefresh = useCallback(() => {
    getPatients(pageNumber, recordsPerPage, {
      birthSex: birthSexFilter,
      dateOfBirth: dateOfBirthFilter,
      mrn: mrnInputFilter,
      patientName: nameInputFilter,
      sortDirection: sortDirection,
      sortField: sortField,
    })
  }, [
    birthSexFilter,
    dateOfBirthFilter,
    getPatients,
    mrnInputFilter,
    nameInputFilter,
    pageNumber,
    recordsPerPage,
    sortDirection,
    sortField,
  ])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const onBirthSexFilterUpdated = useCallback(
    (updatedBirthSex) => {
      dispatch(PatientListState.actions.setPageNumber(1))
      dispatch(PatientListState.actions.setBirthSex(updatedBirthSex))
    },
    [dispatch],
  )

  const onDateOfBirthFilterUpdated = useCallback(
    (updatedDateOfBirth) => {
      dispatch(PatientListState.actions.setPageNumber(1))
      dispatch(
        PatientListState.actions.setDateOfBirthFilter(updatedDateOfBirth),
      )
    },
    [dispatch],
  )

  const onMrnFilterUpdated = useCallback(
    (updatedMrn) => {
      dispatch(PatientListState.actions.setPageNumber(1))
      dispatch(PatientListState.actions.setMrnInputFilter(updatedMrn))
    },
    [dispatch],
  )

  const onNameFilterUpdated = useCallback(
    (updatedName) => {
      dispatch(PatientListState.actions.setPageNumber(1))
      dispatch(PatientListState.actions.setNameInputFilter(updatedName))
    },
    [dispatch],
  )

  const onSetPage = useCallback(
    (updatedPageNumber) => {
      dispatch(PatientListState.actions.setPageNumber(updatedPageNumber))
    },
    [dispatch],
  )

  const onPerPageSelect = useCallback(
    (updatedRecordsPerPage) => {
      dispatch(
        PatientListState.actions.setRecordsPerPage(updatedRecordsPerPage),
      )
      dispatch(PatientListState.actions.setPageNumber(1))
    },
    [dispatch],
  )

  const onSetSort = useCallback(
    (newField: string, newDirection: string) => {
      dispatch(PatientListState.actions.setPageNumber(1))
      dispatch(PatientListState.actions.setSort(newField, newDirection))
    },
    [dispatch],
  )

  const onClearAllFilters = useCallback(() => {
    dispatch(PatientListState.actions.resetFilters())
  }, [dispatch])

  const handleCreateNewPatient = useCallback(() => {
    history.push(`/clinic/patient/new`)
  }, [history])

  const handlePatientSelected = useCallback(
    (patient: IPatient) => {
      history.push(`/clinic/patient/${patient.id}`)
    },
    [history],
  )

  return (
    <PageSection className="pf-m-light">
      <PatientList
        patients={patients}
        onRefresh={onRefresh}
        onPatientSelected={handlePatientSelected}
        filters={{
          birthSexFilter,
          dateOfBirthFilter,
          mrnFilter: mrnInputFilter,
          nameFilter: nameInputFilter,
          onBirthSexFilterUpdated,
          onDateOfBirthFilterUpdated,
          onNameFilterUpdated,
          onMrnFilterUpdated,
        }}
        onPrimaryAction={handleCreateNewPatient}
        pagination={{
          onSetPage,
          onPerPageSelect,
          recordsPerPage,
          pageNumber,
          totalNumberOfRecords,
        }}
        onClearAllFilters={onClearAllFilters}
        primaryActionLabel="Create New Patient"
        sorting={{
          sortDirection,
          sortField,
          onSetSort,
        }}
      />
    </PageSection>
  )
}
